package com.appsyde.shared.app.presentation.ticket

import com.appsyde.shared.modules.ai.domain.model.AiExtractV2
import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2
import com.appsyde.shared.modules.ai.domain.model.parseKeysJsonToSet
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondState

/**
 * Derived data for building TicketState.Success from respond + segment + extract.
 *
 * Rules of truth: missingKeys, toConfirmKeys, sidebarDetected are always derived from
 * [respond.ticket] (ticket-level state). The API returns an "effective" extract with these
 * lists aligned to the ticket; the UI must never use extract.detected / extract.missing /
 * extract.toConfirm for sidebar or blocking state—only for optional enrichment (e.g. one
 * detected field when intent is missing_item).
 */
data class LoadedTicketPayload(
    val missingKeys: List<String>,
    val toConfirmKeys: List<String>,
    val sidebarDetected: List<DetectedFieldV2>,
    val initialReplyText: String
)

/**
 * Builds payload for TicketState.Success. Missing/to_confirm/detected come from ticket only.
 */
fun buildLoadedTicketPayload(
    respond: TicketRespondState,
    segment: ActiveSegment,
    extract: AiExtractV2?
): LoadedTicketPayload {
    val lastMsg = segment.messages.lastOrNull()
    val isPassiveWaitingClient = lastMsg?.direction == Direction.OUTBOUND
    val initialReplyText = when {
        isPassiveWaitingClient -> ""
        else -> respond.ticket.currentDraftSuggestion?.takeIf { it.isNotBlank() }
            ?: extract?.response?.takeIf { it.isNotBlank() }
            ?: respond.draft?.body?.takeIf { it.isNotBlank() }
            ?: ""
    }

    // Source of truth: ticket-level only (PATCH memory/confirm update these; extract is a snapshot and must not re-add resolved keys)
    val ticketMissingSet = parseKeysJsonToSet(respond.ticket.missingKeysJson)
        .map { it.trim().lowercase() }
        .filter { it.isNotBlank() }
        .toSet()
    val ticketToConfirmSet = parseKeysJsonToSet(respond.ticket.toConfirmKeysJson)
        .map { it.trim().lowercase() }
        .filter { it.isNotBlank() }
        .toSet()
    val emailKnown = !respond.ticket.clientEmail.isNullOrBlank()
    val customerEmailExcluded = if (emailKnown) setOf("customer_email") else emptySet()
    val missingKeysList = ticketMissingSet
        .minus(customerEmailExcluded)
        .toList()
        .sorted()
    val toConfirmKeysList = ticketToConfirmSet
        .minus(customerEmailExcluded)
        .toList()
        .sorted()
    var sidebarDetected = TicketDetectedFromMemory.parseAndEnrich(
        respond.ticket.detectedMemoryJson,
        respond.ticket.clientEmail
    )
    val sourcesMap = TicketDetectedFromMemory.parseDetectedSourcesJson(respond.ticket.detectedSourcesJson)
    if (sourcesMap.isNotEmpty()) {
        sidebarDetected = sidebarDetected.map { f ->
            val k = f.key?.trim()?.lowercase()
            if (k != null && sourcesMap.containsKey(k)) f.copy(source = sourcesMap[k]) else f
        }
    }
    val intentNorm = extract?.intent?.trim()?.lowercase()
    if (intentNorm == "missing_item") {
        // Ne pas afficher "Article manquant" (issue_type) dans Données utiles : déjà dans le titre du ticket
        sidebarDetected = sidebarDetected.filter { it.key?.trim()?.lowercase() != "issue_type" }
        // Une seule ligne "Manquant" avec l'article manquant ; ne pas dupliquer si déjà présente (ex. mémoire ticket)
        val alreadyHasManquant = sidebarDetected.any { it.key?.trim()?.lowercase() == "missing_product_name" }
        if (!alreadyHasManquant && extract != null) {
            val detectedByKey = extract.detected.associate { it.key?.trim()?.lowercase().orEmpty() to it.value?.trim().orEmpty() }
            val missingProductValue = detectedByKey["missing_product_name"]
                ?.takeIf { it.isNotBlank() }
                ?: detectedByKey["product_name"]
                    ?.takeIf { it.isNotBlank() }
            if (!missingProductValue.isNullOrBlank()) {
                sidebarDetected = sidebarDetected + DetectedFieldV2(
                    key = "missing_product_name",
                    label = "Manquant",
                    value = missingProductValue,
                    status = "detected"
                )
            }
        }
    }
    return LoadedTicketPayload(
        missingKeys = missingKeysList,
        toConfirmKeys = toConfirmKeysList,
        sidebarDetected = sidebarDetected,
        initialReplyText = initialReplyText
    )
}
